"use client";

import React, { useState, useMemo } from "react";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import type { PieChartProps, PieChartDataItem } from "./types";
import { NumberUtils } from "@/utils/NumberUtils";
import { NoDataView } from "../NoDataView";
import { Button } from "@/components/atoms";
import { Switch } from "@/components/molecules/Switch";

// Helper function for class names
function classNames(...classes: (string | boolean | undefined)[]) {
    return classes.filter(Boolean).join(" ");
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        name: string;
        value: number;
        payload: PieChartDataItem;
    }>;
    valueFormatter?: (value: number) => string;
}

const CustomTooltip = ({ active, payload, valueFormatter }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
        const entry = payload[0];
        const color = entry.payload.color || "#8884d8";

        return (
            <div className="bg-white px-3 py-2 border border-gray-200 shadow-lg rounded-md">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                    <span className="font-medium text-gray-900 text-sm">{entry.name}</span>
                </div>
                <div className="font-semibold text-sm text-gray-900">{valueFormatter ? valueFormatter(entry.value) : entry.value}</div>
                {entry.payload.share && <div className="text-xs text-gray-500 mt-0.5">{entry.payload.share}</div>}
            </div>
        );
    }
    return null;
};

// Default value formatter
function defaultValueFormatter(number: number) {
    const formatter = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
    return formatter.format(number);
}

// Constants
const DEFAULT_COLORS = [
    "#3366CC",
    "#DC3912",
    "#FF9900",
    "#109618",
    "#990099",
    "#0099C6",
    "#DD4477",
    "#AAAA11",
    "#6633CC",
    "#B82E2E",
    "#14b8a6",
    "#6366f1",
    "#3B3EAC",
    "#341cac",
    "#66AA00",
    "#316395",
    "#429795",
    "#7dd3c0",
    "#994499",
];

const CHART_CONFIG = {
    innerRadius: 80,
    outerRadius: 120,
    paddingAngle: 0,
    animationDuration: 400,
    chartHeight: 300,
} as const;

// Custom label for center of donut chart
const renderCenterLabel = (centerLabel?: string, centerValue?: string) => {
    if (!centerLabel && !centerValue) return null;

    return (
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontFamily="Roboto, sans-serif">
            <tspan x="50%" dy="-0.5em" className="text-2xl font-semibold fill-gray-900">
                {centerValue}
            </tspan>
            <tspan x="50%" dy="1.5em" className="text-sm fill-gray-600">
                {centerLabel}
            </tspan>
        </text>
    );
};

export default function PieChart({
    widgetFiltersController,
    data,
    className = "",
    valueFormatter = defaultValueFormatter,
    showLegend = false,
    showList = true,
    showTooltip = true,
    colors = DEFAULT_COLORS,
    donut = true,
    isVerticalLayout = false,
    compare,
    widget,
    hiddenSeries = new Set<string>(),
}: PieChartProps) {
    const [showPreviousData, setShowPreviousData] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const metadata = data?.metadata || {};
    // Check if legends should be hidden via widget metadata
    const hideLegends = widget?.metadata?.chart?.hideLegends === true;
    // Use totalSeriesLabel from widget metadata if available, otherwise fall back to data metadata
    const finalCenterLabel = widget?.metadata?.totalSeriesLabel || metadata.centerLabel;
    const finalCenterValue = metadata.centerValue;
    const chartData = data?.data || [];

    // Check if this is a spend/cost metric
    const isSpendMetric = NumberUtils.isCurrencyMetric(finalCenterLabel || '');
    // const chartData = useMemo(() => {
    //     return (data?.data ?? []).filter(item => {
    //         if (!widgetFiltersController?.selectedDimensionItems) return true;
    //         return widgetFiltersController?.selectedDimensionItems.some(selected => selected.text === item.name);
    //     })
    // }, [data?.data, widgetFiltersController?.selectedDimensionItems]);

    // Assign colors and filter visible data in one pass
    const { compareData, dataWithColors, visibleData, visibleTotal, shareMap } = useMemo(() => {
        // Add colors to all items
        const withColors = chartData.map((item: PieChartDataItem, index) => ({
            ...item,
            value: NumberUtils.round(item.value),
            color: item.color || colors[index % colors.length],
        }));

        const _compareData = compare?.unified?.data?.map((item, index) => ({ name: item.identifiers?.name, value: item.previous?.value, color: colors?.[index % colors.length] })) || [];

        // Filter visible items
        const visible = withColors.filter((item) => !hiddenSeries.has(item.name));
        const visibleCompare = _compareData?.filter((item) => !hiddenSeries.has(item.name));
        const total = visible.reduce((sum, item) => sum + (item.value), 0);
        const totalCompare = visibleCompare.reduce((sum, item) => sum + (item.value), 0);

        // Add share percentages to visible items
        const visibleWithShares = visible.map((item) => ({
            ...item,
            value: NumberUtils.round(item.value),
            share: `${((item.value / total) * 100).toFixed(1)}%`,
        }));

        const visibleCompareWithShares = visibleCompare?.map((item) => ({
            ...item,
            value: NumberUtils.round(item.value),
            share: `${((item.value / totalCompare) * 100).toFixed(1)}%`,
        }));

        // Create a map for quick share lookup
        const shares = new Map(visibleWithShares.map((item) => [item.name, item.share]));

        return {
            compareData: _compareData,
            dataWithColors: withColors,
            visibleData: showPreviousData ? visibleCompareWithShares : visibleWithShares,
            visibleTotal: NumberUtils.round(showPreviousData ? totalCompare : total).toLocaleString(),
            shareMap: shares,
        };
    }, [chartData, colors, hiddenSeries, showPreviousData]);
    
    // Format center value with $ for spend metrics
    const formattedCenterValue = isSpendMetric ? NumberUtils.formatCurrency(visibleTotal) : visibleTotal;

    // Sort legend by value descending
    const sortedDataWithColors = useMemo(() =>
        [...dataWithColors].sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
        [dataWithColors]
    );

    // Pagination logic for vertical layout
    const ITEMS_PER_PAGE = 5;
    const shouldPaginate = isVerticalLayout && sortedDataWithColors.length > ITEMS_PER_PAGE;
    const totalPages = shouldPaginate ? Math.ceil(sortedDataWithColors.length / ITEMS_PER_PAGE) : 1;
    const paginatedData = shouldPaginate
        ? sortedDataWithColors.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE)
        : sortedDataWithColors;

    if (!data || !data.data || data.data.length === 0) {
        return <NoDataView className={className} />;
    }

    // Check if all data values are 0, null, undefined, or non-numeric
    const hasMeaningfulData = data.data.some(item =>
        item.value != null && item.value !== 0 && typeof item.value === 'number'
    );

    if (!hasMeaningfulData) {
        return <NoDataView className={className} />;
    }

    const layoutClasses = isVerticalLayout
        ? "flex flex-col gap-4 items-stretch h-full"
        : "flex flex-col lg:flex-row gap-4 items-center h-full max-w-6xl mx-auto";

    const chartWrapperClasses = isVerticalLayout
        ? "w-full flex justify-center"
        : "w-full lg:w-1/2 flex justify-center";

    const listWrapperClasses = isVerticalLayout
        ? "w-full flex justify-center px-6"
        : "w-full lg:w-1/2 pr-6 px-2";

    return (
        <div className={classNames("w-full h-full ", donut ? (isVerticalLayout ? "mb-4" : "mb-6") : "", className)}>
            {compare && <div className="flex items-center justify-end text-sm text-gray-600 w-full px-6" style={{ fontFamily: "Roboto, sans-serif" }}>
                <Button onClick={() => setShowPreviousData(!showPreviousData)} variant="secondary">
                    <div className="flex items-center justify-between text-sm" style={{ fontFamily: "Roboto, sans-serif" }}>
                        <Switch checked={!showPreviousData} onChange={() => setShowPreviousData(!showPreviousData)} label={["Previous", "Current"]}/>
                    </div>
                </Button>
            </div>}
            <div className={layoutClasses}>
                {/* Chart */}
                <div className={chartWrapperClasses}>
                    <ResponsiveContainer width="100%" height={CHART_CONFIG.chartHeight}>
                        <RechartsPieChart>
                            {showTooltip && <Tooltip content={<CustomTooltip valueFormatter={valueFormatter} />} />}
                            {showLegend && <Legend verticalAlign="bottom" height={36} iconSize={10} />}
                            <Pie
                                data={visibleData}
                                cx="50%"
                                cy="50%"
                                innerRadius={donut ? CHART_CONFIG.innerRadius : 0}
                                outerRadius={CHART_CONFIG.outerRadius}
                                paddingAngle={CHART_CONFIG.paddingAngle}
                                dataKey="value"
                                label={false}
                                animationBegin={0}
                                animationDuration={CHART_CONFIG.animationDuration}
                                animationEasing="ease-out"
                            >
                                {visibleData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                ))}
                                {donut && renderCenterLabel(finalCenterLabel, formattedCenterValue)}
                            </Pie>
                        </RechartsPieChart>
                    </ResponsiveContainer>
                </div>

                {/* List */}
                {showList && !hideLegends && (
                    <div className={listWrapperClasses}>
                        <div className={isVerticalLayout ? "w-full max-w-2xl mb-4" : ""}>
                            <div
                                className={classNames(
                                    "grid items-center text-sm text-gray-600 mb-3 px-2",
                                    compare ? "grid-cols-[minmax(0,1fr)_90px_90px_90px]" : "grid-cols-[minmax(0,1fr)_90px_90px]",
                                )}
                                style={{ fontFamily: "Roboto, sans-serif" }}
                            >
                                <span>Type</span>
                                {compare && <span className="text-right">Previous</span>}
                                <span className="text-right">{compare ? "Current" : "Amount"}</span>
                                <span className="text-right">Share</span>
                            </div>
                            <ul className="space-y-0.25">
                                {(shouldPaginate ? paginatedData : sortedDataWithColors).map((item) => {
                                    const isHidden = hiddenSeries.has(item.name);
                                    const share = shareMap.get(item.name) || "—";

                                    const compareItem = compareData?.find((dataItem) => dataItem.name === item.name);
                                    const previousAmount = compareItem?.value;
                                    const hasPreviousAmount = previousAmount !== undefined && previousAmount !== null;

                                    return (
                                        <li
                                            key={item.name}
                                            className={classNames(
                                                "relative grid items-center py-2.5 px-2 rounded-lg transition-all duration-200 select-none",
                                                compare ? "grid-cols-[minmax(0,1fr)_90px_90px_90px]" : "grid-cols-[minmax(0,1fr)_90px_90px]",
                                                isHidden ? "bg-gray-50 hover:bg-gray-100" : "hover:bg-gray-50",
                                            )}
                                            style={{
                                                opacity: isHidden ? 0.5 : 1,
                                            }}
                                        >
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 transition-opacity duration-200"
                                                    style={{
                                                        backgroundColor: item.color,
                                                        opacity: isHidden ? 0.3 : 1,
                                                    }}
                                                    aria-hidden="true"
                                                />
                                                <span
                                                    className={classNames("truncate transition-all duration-200", isHidden ? "text-gray-400" : "text-gray-900")}
                                                    style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}
                                                >
                                                    {item.name}
                                                </span>
                                            </div>
                                            {compare && (
                                                <span
                                                    className={classNames(
                                                        "font-semibold tabular-nums transition-all duration-200 text-right",
                                                        isHidden ? "text-gray-400" : "text-gray-900",
                                                    )}
                                                    style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}
                                                >
                                                    {hasPreviousAmount
                                                        ? isSpendMetric
                                                            ? NumberUtils.formatCurrency(previousAmount as number)
                                                            : valueFormatter(previousAmount as number)
                                                        : "—"}
                                                </span>
                                            )}
                                            <span
                                                className={classNames(
                                                    "font-semibold tabular-nums transition-all duration-200 text-right",
                                                    isHidden ? "text-gray-400" : "text-gray-900",
                                                )}
                                                style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}
                                            >
                                                {isSpendMetric ? NumberUtils.formatCurrency(item.value) : valueFormatter(item.value)}
                                            </span>
                                            <span
                                                className={classNames(
                                                    "text-xs font-medium px-2.5 py-1 rounded-md tabular-nums min-w-[3.5rem] text-center justify-self-end transition-all duration-200",
                                                    isHidden ? "text-gray-400 bg-gray-200" : "text-gray-600 bg-gray-100",
                                                )}
                                            >
                                                {share}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            
                            {/* Pagination dots for vertical layout */}
                            {shouldPaginate && (
                                <div className="flex items-center justify-center gap-1.5 mt-4">
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentPage(index)}
                                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                                currentPage === index 
                                                    ? 'bg-cyan-500' 
                                                    : 'bg-gray-300 hover:bg-gray-400'
                                            }`}
                                            aria-label={`Go to page ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Description */}
            {metadata.description && <p className="text-sm text-gray-600 mt-4">{metadata.description}</p>}
        </div>
    );
}
